import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { Box, Card, CardContent, Typography, Alert } from '@mui/material'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { lessonSchedulesApi } from '@/api/lessonSchedules'
import { ScheduleReadonlyPreview } from '@/components/lessonSchedule/ScheduleReadonlyPreview'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import type { LessonScheduleTemplate } from '@/types'
import '@/styles/dashboard.css'

/**
 * Просмотр расписания для ученика в разделе профиля.
 */
export function StudentSchedulePage() {
  const { t } = useTranslation()
  const authUser = useAuthStore((s) => s.user)
  const { currentUser, fetchUser } = useUserStore()
  const userId = authUser?.id
  const groupId = currentUser?.contract?.group_id ?? null

  const [schedule, setSchedule] = useState<LessonScheduleTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (userId) {
      fetchUser(userId).catch(() => {})
    }
  }, [userId, fetchUser])

  useEffect(() => {
    if (!userId || !currentUser) return
    setLoading(true)
    setError(null)
    const run = async () => {
      try {
        if (groupId) {
          const s = await lessonSchedulesApi.getGroupSchedule(groupId)
          setSchedule(s)
        } else {
          const s = await lessonSchedulesApi.getUserSchedule(userId)
          setSchedule(s)
        }
      } catch (e: unknown) {
        const err = e as { message?: string }
        setError(err.message || t('lessonSchedules.loadError'))
        setSchedule(null)
      } finally {
        setLoading(false)
      }
    }
    void run()
  }, [userId, currentUser, groupId, t])

  return (
    <ProfileLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('lessonSchedules.studentPageTitle', 'Моё расписание')}
          </Typography>
          <PageHelpPopover pageKey="lesson-schedules" />
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('lessonSchedules.studentPageSubtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent>
            <ScheduleReadonlyPreview schedule={schedule} loading={loading} />
          </CardContent>
        </Card>
      </Box>
    </ProfileLayout>
  )
}
